<script setup lang="ts">
import { storeToRefs } from "pinia";
import { computed, nextTick, onMounted, ref, toRaw } from "vue";

import { useToast } from "vue-toastification";
import { useRoute } from "vue-router";

import { UpdateMaterialPropertiesRequest } from "@/api/services/Mapping";

import { useProject } from "@/stores/project/project.store";
import { useModalStore } from "@/stores/modals/modals.store";

import Button from "@/components/Reusables/Buttons/Button.vue";

import LifetimeTable from "@/components/Views/MaterialMapping/LifetimeTable/LifetimeTable.vue";
import NumberInput from "@/components/Reusables/Inputs/NumberInput.vue";

import { SpeckleMaterial } from "@/types/Views/MappingWizard/SpeckleMaterial";

import TypeMappingForm from "./TypeMappingForm.vue";
import MaterialMappingForm from "./MaterialMappingForm.vue";
import { resolveByType } from "./resolveByType";
import { Country_Enum } from "@/graphql";
import { useLocalization } from "@/providers/Localization";
import { useDashboard } from "@/stores/dashboard/dashboard.store";
import { useProjectEditReadOnly } from "@/pages/ProjectVersionHistory/composables/useProjectEditReadOnly.composable";
import RevisionGatedButton from "@/pages/ProjectVersionHistory/components/revisions/RevisionGatedButton.vue";

const route = useRoute();

const dashboardStore = useDashboard();
const { refreshDashboard } = dashboardStore;

const projectStore = useProject();

const { project } = storeToRefs(projectStore);
const { isGroupingByType } = storeToRefs(projectStore);
const localization = useLocalization();
const { country } = storeToRefs(localization);
const modals = useModalStore();

// byType defaults to undefined: Vue casts an absent optional boolean prop to false, and undefined
// is the only default that keeps "not passed" distinguishable from "passed false" (resolveByType.ts).
const props = withDefaults(
  defineProps<{
    mappedMaterialId?: string;
    materials: SpeckleMaterial[];
    library: any;
    /** Grouping the material ids belong to; defaults to the page's current grouping. */
    byType?: boolean;
  }>(),
  { byType: undefined }
);

const activeMaterialForm = ref(0);
const cloneMaterials = ref<SpeckleMaterial[]>(JSON.parse(JSON.stringify(toRaw(props.materials))));
const currentMaterialIndex = ref<number>(0);

const cloneMaterial = computed(() => {
  const material = cloneMaterials.value[currentMaterialIndex.value];
  return material || null;
});

const lifeTimeTableIsHidden = ref<boolean>(false);
const alternateBimTypeCode = ref<string>("");
const isSaving = ref<boolean>(false);

// This view only opens from a drop, and drops are already blocked for a Viewer and while a frozen
// revision is previewed — but it can also still be open when the user switches into a preview, and
// for a construction it auto-submits on mount. So the save is guarded here too, on both reasons at
// once (RTLCA-5253, RTLCA-5582). The Save button used to disappear entirely for a Viewer; it now
// stays put and says why, like every other blocked action on this page.
const { blockedByPreview } = useProjectEditReadOnly();

const submitSave = async () => {
  if (blockedByPreview()) return;

  isSaving.value = true;

  try {
    const payload = cloneMaterials.value.map((material) => {
      let preferredUnit = material.totalDeclaredUnits?.find((u: any) => u.preferred);

      if (preferredUnit) {
        if (preferredUnit.newValue != null) {
          preferredUnit.value = preferredUnit.newValue;
        }
      }

      const declaredUnitRequest = preferredUnit
        ? {
            declaredUnit: preferredUnit.declaredUnit,
            preferred: true,
            value: preferredUnit.newValue != null ? preferredUnit.newValue : preferredUnit.value ?? 0,
            source: preferredUnit.source ?? 0,
          }
        : undefined;

      return {
        materialId: material.id!,
        ...(!props.library.isConstruction && { lifetime: material.lifeTime }),
        ...(declaredUnitRequest && { declaredUnitRequest }),
        includeMapping: true,
        mappedMaterialId: props.mappedMaterialId,
      };
    });

    const res = await UpdateMaterialPropertiesRequest(
      String(route.params.id),
      resolveByType(props.byType, isGroupingByType.value),
      payload
    );
    if ("materials" in res) {
      refreshDashboard();
      modals.close(res.materials);
    }
  } catch (error) {
    console.error("Error saving materials:", error);
    useToast().error("Failed to save materials. Please try again.");
  } finally {
    isSaving.value = false;
  }
};

const handleLifeTimeSelected = (value: string) => {
  cloneMaterials.value[activeMaterialForm.value].lifeTime = Number(value);
  lifeTimeTableIsHidden.value = false;
};

const cancelMapping = async () => {
  modals.close(false);
};

const materialTitles = ref(
  cloneMaterials.value.map((material) => ({
    id: material.id,
    identifier: material.identifier,
    isValid: false,
  }))
);

const enableSave = computed(() => {
  return materialTitles.value.every((material) => material.isValid);
});

const handleMaterialValid = (isValid: boolean, i: number) => {
  materialTitles.value[i].isValid = isValid;
};

const assignToAllValue = ref<string>("");

const assignToAll = () => {
  if (assignToAllValue.value) {
    cloneMaterials.value.map((material) => (material.lifeTime = Number(assignToAllValue.value)));
  }
};

// Countries that have their own built-in lifetime table (rendered via the legacy
// `LifetimeTable` view) rather than the new buildingPartServiceLifetime picker.
const COUNTRIES_WITHOUT_LIFETIME_PICKER = [Country_Enum.DENMARK];

const toggleLifetimeTable = async (i: number) => {
  activeMaterialForm.value = i;

  if (country.value && !COUNTRIES_WITHOUT_LIFETIME_PICKER.includes(country.value)) {
    const material = cloneMaterials.value[i];
    const speckleParameter = project.value?.speckleConfiguration.parameters.classificationCode;

    if (!material) {
      useToast().error(
        "Could not open lifetable table - material not found. Please try again or contact support if the issue persists."
      );
      return;
    }

    const classificationCode = speckleParameter ? material?.additionalParameters[speckleParameter] : null;
    const materialCategoryId = material?.mapping?.category;

    const result = await modals.open(
      "MaterialServiceLifetimeSelectorModal",
      { country: country.value, classificationCode, materialCategoryId },
      { stackable: true, wrapperClass: "max-w-2xl  h-full" }
    );

    if (result && result.lifetime) {
      handleLifeTimeSelected(result.lifetime);
    }
  } else {
    lifeTimeTableIsHidden.value = true;
  }
};

onMounted(async () => {
  const isValid = enableSave.value;
  const isConstruction = props.library.isConstruction;

  if (isConstruction && isValid && !isSaving.value) {
    await nextTick(async () => {
      submitSave();
    });
  }
});
</script>

<template>
  <div class="flex flex-col">
    <div
      v-show="!lifeTimeTableIsHidden"
      class="flex h-full">
      <div class="flex flex-1 flex-col">
        <p class="pb-1">Set missing properties to complete mapping</p>

        <div
          v-if="cloneMaterials.length > 1 && !library.isConstruction"
          class="justify-left rounded-4 mb-2 flex gap-3 bg-slate-100 p-3 pb-2 text-sm font-bold text-slate-500">
          <NumberInput
            v-model:value="assignToAllValue"
            color="primary"
            placeholder="Enter lifetime"
            :max="300"
            positive
            :no-decimals="true"
            class="max-w-56"
            @keyup.enter="assignToAll" />
          <Button
            label="Assign to all"
            color="button"
            @click="assignToAll" />
        </div>

        <template v-if="library.isConstruction">
          <TypeMappingForm
            v-for="(clonedMat, i) in cloneMaterials"
            :key="i"
            :material="clonedMat"
            :material-count="cloneMaterials.length"
            :index="i"
            :library="library"
            @is-material-valid="handleMaterialValid"
            @toggle-lifetime-table="() => toggleLifetimeTable(i)"
            @update-lifetime="clonedMat.lifeTime = $event" />
        </template>
        <template v-else>
          <MaterialMappingForm
            v-for="(clonedMat, i) in cloneMaterials"
            :key="i"
            :material="clonedMat"
            :material-count="cloneMaterials.length"
            :index="i"
            :library="library"
            @is-material-valid="handleMaterialValid"
            @toggle-lifetime-table="() => toggleLifetimeTable(i)"
            @update-lifetime="clonedMat.lifeTime = $event" />
        </template>
        <div class="flex justify-between">
          <Button
            label="Cancel"
            color="button"
            @click="cancelMapping" />

          <RevisionGatedButton
            :label="isSaving ? 'Saving...' : cloneMaterials.length > 1 ? 'Save All' : 'Save'"
            :is-loading="isSaving"
            :disabled="!enableSave"
            color="primary"
            :guard="useProjectEditReadOnly"
            @click="submitSave" />
        </div>
      </div>
    </div>

    <LifetimeTable
      v-if="lifeTimeTableIsHidden"
      :title="cloneMaterial.identifier"
      :focus-construction-id="cloneMaterial.additionalParameters?.BIMTypeCode"
      :focus-construction-id-alternate="alternateBimTypeCode"
      @clear-select="lifeTimeTableIsHidden = false"
      @life-time-selected="
        (data: any) => {
          handleLifeTimeSelected(data.value);
        }
      " />
  </div>
</template>
